import React, { useContext, useMemo } from 'react';
import loadable from '@loadable/component';
import styled from 'styled-components';
import Container from 'ls-common-client/dist/components/Container';
import { Redirect } from 'react-router-dom';
import Animation from 'ls-common-client/dist/components/Animation';
import slugify from '../../utils/slugify';
import profileLoader from '../../animations/profileLoader.json';
import { Context } from '../../context/AppContext';
import AddABusiness from '../../components/AddABusiness';
import LazyLoad from '../../components/LazyLoad';
import CenteredLoader from '../../components/CenteredLoader';
import ScrollToTop from '../../components/ScrollToTop';
import ReviewForm from '../../components/ReviewForm';
import Top from '../../components/Landing/Top';
import LayoutContainer from '../../components/LayoutContainer';
import useSsr from '../../hooks/useSsr';
import categoryUrl from '../../utils/categoryUrl';
import ArcBackground from '../../components/Landing/Background/Arc';
import About from './About';
import Faq from './Faq';
import { categoryQuery } from '../../gql/search';
import Meta from './Meta';
import redirects from './redirects';
import descriptions from './descriptions';
import './styles.css';
import UrlList from '../../components/Landing/UrlList';
import HoverIcon from '../../components/HoverIcon';
import NextLink from '../../components/NextLink';
import urls from './Urls';
import getLatestCategoryPath from '../../constants/legacy/getLatestCategoryPath';

const NotFound = loadable(() => import('../NotFound'));
const AppDownload = loadable(
  () => import('../../components/Landing/AppDownload'),
  {
    fallback: <CenteredLoader />,
  }
);
const Breadcrumbs = loadable(() => import('../../components/Breadcrumbs'));
const DigitalMarketingBanner = loadable(
  () => import('../../components/DigitalMarketingBanner'),
  {
    fallback: <CenteredLoader />,
  }
);
const PopularIndustriesList = loadable(
  () => import('../../components/Landing/PopularIndustries/List'),
  {
    fallback: <CenteredLoader />,
  }
);
const ListYourBusiness = loadable(
  () => import('../../components/Landing/ListYourBusiness'),
  {
    fallback: <CenteredLoader />,
  }
);
const TopRatedBusinesses = loadable(
  () => import('../../components/Landing/TopRatedBusinesses'),
  {
    fallback: <CenteredLoader />,
  }
);
const FeatureReviews = loadable(
  () => import('../../components/Landing/FeatureReviews'),
  {
    fallback: <CenteredLoader />,
  }
);
const LocalBusinessInfo = loadable(
  () => import('../../components/Landing/LocalBusinessInfo'),
  {
    fallback: <CenteredLoader />,
  }
);

const StyledRoot = styled(Container)`
  background: #ffffff;
`;

const ssr = ({ params, redirect }) => {
  const { categoryName } = params;

  return [
    {
      name: 'SeoCategory',
      query: categoryQuery,
      variables: {
        path: categoryName,
      },
      callback: (data, errors) => {
        const url = redirects({ params, data, errors });
        return url ? redirect(url) : false;
      },
    },
  ];
};

const Category = ({ match }) => {
  const { url } = match || {};

  const { SeoCategory, redirectUrl, loading } = useSsr(ssr);

  const {
    userLocation: { location },
    media: { mobile, desktop },
  } = useContext(Context);

  const { name: suburb } = location || {};

  const { name: categoryName, id: categoryId, relatedCategories = [] } =
    SeoCategory?.categories?.edges?.[0]?.node || {};

  const categoryAbout = useMemo(() => descriptions[categoryName], [
    descriptions,
    categoryName,
  ]);

  const path = useMemo(() => getLatestCategoryPath(slugify(categoryName)), [
    categoryName,
  ]);

  const initialCategory = {
    id: categoryId,
    label: categoryName,
    path: path || slugify(categoryName),
  };

  if (redirectUrl)
    return (
      <StyledRoot>
        <Container width="150px" margin="0 auto">
          <Animation data={profileLoader} width="150px" />
        </Container>
        <Redirect to={redirectUrl} />
      </StyledRoot>
    );

  if (!categoryName && !loading) {
    return <NotFound />;
  }

  return (
    <StyledRoot className="Category__root">
      <ScrollToTop />
      <Meta category={categoryName} canonical={url} />
      <Top
        defaultCategoryValue={categoryName}
        searchProps={{ initialCategory }}
        heading={
          loading ? null : (
            <>
              <span>Find the best local</span> {categoryName}{' '}
              <span>near me</span>
            </>
          )
        }
      />
      {!loading && (
        <>
          <ArcBackground>
            <Container marginTop={desktop ? '-250px' : 0} zIndex="999">
              {urls.map(({ title, links }) => (
                <UrlList
                  key={`${categoryName} in ${title}`}
                  title={`Popular Searches for ${categoryName} in ${title}`}
                  data={links}
                  columns={3}
                  maxWidth="1350px"
                  embedTitle
                  renderItem={item => (
                    <HoverIcon>
                      <NextLink
                        to={`${categoryUrl(categoryName)}/${item.path}`}
                      >
                        {categoryName} in {item.anchorText}
                      </NextLink>
                    </HoverIcon>
                  )}
                />
              ))}
            </Container>

            <LazyLoad>
              {!!categoryName && (
                <About
                  categoryName={categoryName}
                  description={categoryAbout}
                  title={`About ${categoryName} in Australia`}
                  b2bStyle={mobile ? 'vertical' : 'horizontal'}
                />
              )}
            </LazyLoad>
          </ArcBackground>
          <LocalBusinessInfo
            title={` All the information you need for local ${categoryName} in one place.`}
            subTitle={`Locals are searching for, reviewing and contacting ${categoryName} on Localsearch right now! See how people are engaging with them:`}
          />
          <Faq
            title={`FAQ's about ${categoryName} in Australia`}
            categoryName={categoryName}
          />
          <LazyLoad style={{ minHeight: '400px' }}>
            {!!categoryId && (
              <FeatureReviews
                title={`Featured Reviews for ${categoryName} in Australia`}
                params={{
                  categoryId,
                }}
              />
            )}
            <TopRatedBusinesses
              params={{ first: 4, categoryId }}
              title={`Top-rated ${categoryName} near you in Australia`}
              subTitle={`Check out these popular ${categoryName} near you`}
            />
            {relatedCategories && !!relatedCategories.length && (
              <LayoutContainer
                style={{
                  margin: '80px auto',
                }}
              >
                <div className="Category__section">
                  <h3 className="Category__heading">
                    Related categories to {categoryName}
                  </h3>
                  <PopularIndustriesList
                    data={relatedCategories.map(item => ({
                      name: item.name,
                    }))}
                    categoryPageLink
                    limit={50}
                    arrows
                  />
                </div>
              </LayoutContainer>
            )}
          </LazyLoad>
          <LazyLoad style={{ minHeight: '400px' }}>
            <AppDownload />
            <ListYourBusiness
              title={`List your business in ${suburb || 'Australia'}`}
              text={`${suburb ||
                'Australia'} locals are just waiting to find your business on Localsearch. All it takes to add your business is completing the super quick and easy form—click the button below to go there now. What are you waiting for?`}
            />
            <DigitalMarketingBanner />
          </LazyLoad>
          <LayoutContainer className="Category__Breadcrumbs">
            <Breadcrumbs
              data={[
                {
                  breadcrumb: 'Localsearch',
                  url: '/',
                },
                {
                  url: categoryUrl(categoryName),
                  breadcrumb: categoryName,
                },
              ]}
            />
          </LayoutContainer>
        </>
      )}
      <ReviewForm />
      <AddABusiness />
    </StyledRoot>
  );
};

export { ssr };

export default Category;
